import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtNum, fmtPct } from "@/lib/api";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Hoisted chart config — extracted from JSX props so they get one stable
// reference instead of a fresh object on every render (recharts memoization
// then actually kicks in).
const CHART_MARGIN = { top: 10, right: 12, left: 0, bottom: 10 };
const X_AXIS_TICK = { fill: "#64748b", fontSize: 10 };
const Y_AXIS_TICK = { fill: "#64748b", fontSize: 10, fontFamily: "JetBrains Mono" };
const TOOLTIP_CONTENT_STYLE = { background: "#131c2d", border: "1px solid #1e293b", fontSize: 11 };
const TOOLTIP_ITEM_STYLE = { fontFamily: "JetBrains Mono" };
const CHART_BOX_STYLE = { height: 320 };
const xTickFormatter = (v) => (v || "").slice(11, 16);

function Metric({ label, value, positive, testid }) {
  return (
    <div className="surface rounded-sm p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`text-2xl font-mono mt-2 ${positive === true ? "text-pos" : positive === false ? "text-neg" : ""}`}>{value}</div>
    </div>
  );
}

export default function Performance() {
  const [data, setData] = useState(null);
  useEffect(() => {
    // Empty deps intentional — we fetch once on mount. `api` is a module-level
    // singleton (stable ref). Not a lint bug — the analyzer sees setData
    // inside the promise callback and mis-counts.
    let cancelled = false;
    api.get("/performance").then(({ data }) => {
      if (!cancelled) setData(data);
    });
    return () => { cancelled = true; };
  }, []);

  if (!data) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="performance-page">
      <h1 className="text-2xl font-medium tracking-tight">Performance</h1>
      <p className="text-xs text-slate-500 uppercase tracking-[0.15em]">
        All metrics use NET P&amp;L (after charges)
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric label="Net P&L" value={fmtMoney(data.net_pnl)} positive={data.net_pnl >= 0} testid="metric-net-pnl" />
        <Metric label="Total Trades" value={data.total_trades} testid="metric-total-trades" />
        <Metric label="Win Rate" value={fmtPct(data.win_rate)} testid="metric-win-rate" />
        <Metric label="Profit Factor" value={data.profit_factor ? fmtNum(data.profit_factor) : "—"} testid="metric-profit-factor" />
        <Metric label="Expectancy / Trade" value={fmtMoney(data.expectancy)} testid="metric-expectancy" />
        <Metric label="Max Drawdown" value={fmtMoney(data.max_drawdown)} positive={false} testid="metric-drawdown" />
        <Metric label="Gross Profit" value={fmtMoney(data.gross_pnl)} testid="metric-gross" />
        <Metric
          label="Costs / Gross"
          value={data.costs_as_pct_of_gross_profit ? `${data.costs_as_pct_of_gross_profit.toFixed(1)}%` : "—"}
          testid="metric-costs-pct"
        />
      </div>

      <div className="surface rounded-sm p-4" data-testid="equity-curve">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Equity Curve (Net P&amp;L)</div>
        <div style={CHART_BOX_STYLE}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.equity_curve} margin={CHART_MARGIN}>
              <defs>
                <linearGradient id="pnl" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1e293b" strokeDasharray="2 2" />
              <XAxis dataKey="closed_at" tick={X_AXIS_TICK} minTickGap={40} tickFormatter={xTickFormatter} />
              <YAxis tick={Y_AXIS_TICK} width={80} />
              <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
              <Area type="monotone" dataKey="cumulative_net_pnl" stroke="#10b981" strokeWidth={2} fill="url(#pnl)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
